import {
  Button,
  Col,
  DatePicker,
  Divider,
  Drawer,
  Empty,
  Form,
  Input,
  Row,
  Select,
  Tooltip,
  message,
} from 'antd';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { validateDateNotInPast } from 'client/utils/formValidations';
import useChangePackage from '../../hooks/useChangePackage';
import { useCreateTeamForm } from '../../hooks/useCreateTeamForm';
import { useGetSalesforceAccountInfoMutation } from '../../services/hooks/useGetSalesforceAccountInfo';
import { domainValidator } from '../DomainWhitelist';

const { Search } = Input;

type CreateTeamForm = {
  sfAccountId?: string;
  teamName: string;
  packageStart: moment.Moment;
  packageEnd: moment.Moment;
  packageID: number;
  domains: string;
};

type Props = {
  sfAccountId?: string;
  onSuccess?: (idTeam: number) => void;
};

export const CreateNewTeam = ({ sfAccountId, onSuccess }: Props) => {
  const { setSearchPackageQuery, filteredResults } = useChangePackage();
  const { onSubmit } = useCreateTeamForm();
  const [isLoading, setIsLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [lookupAccountId, setLookupAccountId] = useState('');
  const [contractSeats, setContractSeats] = useState<number | undefined>();
  const [form] = Form.useForm<CreateTeamForm>();
  const navigate = useNavigate();

  const { mutateAsync: lookupAccount, isLoading: isLookingUp } =
    useGetSalesforceAccountInfoMutation();

  const showDrawer = () => {
    setOpen(true);
  };

  const onClose = () => {
    setOpen(false);
  };

  const handleLookup = async (
    value: string,
    event?:
      | React.ChangeEvent<HTMLInputElement>
      | React.MouseEvent<HTMLElement>
      | React.KeyboardEvent<HTMLInputElement>
  ) => {
    const isClear =
      event?.type === 'click' &&
      (event.target as HTMLElement)?.tagName === 'INPUT';
    if (isClear) {
      setLookupAccountId('');
      setContractSeats(undefined);
      return;
    }

    const accountId = value.trim();
    setLookupAccountId('');
    setContractSeats(undefined);

    if (!accountId) {
      message.error('Please enter a Salesforce account ID to look up.');
      return;
    }

    try {
      const account = await lookupAccount({ accountId });
      if (!account?.accountName) {
        message.error(`Unable to find an account with ID "${accountId}".`);
        return;
      }
      form.setFieldsValue({ teamName: account.accountName });
      setLookupAccountId(accountId);
      setContractSeats(account.memberCap);
    } catch {
      message.error(`Unable to find an account with ID "${accountId}".`);
    }
  };

  useEffect(() => {
    if (open) {
      form.resetFields();
      setLookupAccountId('');
      setContractSeats(undefined);
      if (sfAccountId) {
        form.setFieldsValue({ sfAccountId });
        handleLookup(sfAccountId);
      }
    }
  }, [open, sfAccountId]);

  const handleSubmit = () => {
    form
      .validateFields()
      .then(async (values) => {
        const accountId = (values.sfAccountId || '').trim();
        const seats =
          accountId && accountId === lookupAccountId
            ? contractSeats
            : undefined;
        try {
          setIsLoading(true);
          const { id_team: idTeam } = await onSubmit(
            values.teamName,
            values.packageID,
            values.packageStart,
            values.packageEnd,
            values.domains,
            accountId,
            seats
          );
          setIsLoading(false);
          if (onSuccess) {
            setOpen(false);
            onSuccess(idTeam);
          } else {
            navigate(`/teams/${idTeam}`);
          }
        } catch (e) {
          setIsLoading(false);
          message.error('Failed to create team.Please try again later.');
        }
      })
      .catch(() => {});
  };

  return (
    <>
      <Button type="default" className="mt-5" onClick={showDrawer}>
        Create New Team
      </Button>
      <Drawer
        title="Create Team"
        placement="bottom"
        closable={false}
        open={open}
        onClose={onClose}
        size="large"
      >
        <Form form={form}>
          <Row>
            <Col span={6}>
              <Row>Salesforce account ID </Row>
              <Divider className="border-t-2 border-black border-solid" />
              <div className="p-3 h-24">
                <Form.Item name="sfAccountId" help={false}>
                  <Search
                    placeholder="Fetch data from Salesforce"
                    allowClear
                    enterButton="Lookup"
                    size="large"
                    loading={isLookingUp}
                    onSearch={handleLookup}
                  />
                </Form.Item>
              </div>
            </Col>
          </Row>
          <Row>
            <Col span={6}>
              <Row>Team Name</Row>
              <Divider className="border-t-2 border-black border-solid" />
              <div className="p-3 h-24">
                <Form.Item
                  name="teamName"
                  rules={[{ required: true }]}
                  help={false}
                >
                  <Input className="!h-12" placeholder="Team Name" />
                </Form.Item>
              </div>
              <Divider className="border-t-2 border-black border-solid" />
            </Col>
            <Col span={3}>
              <Row>Start Date</Row>
              <Divider className="border-t-2 border-black border-solid" />
              <div className="p-3 h-24">
                <Form.Item
                  name="packageStart"
                  rules={[
                    { required: true },
                    {
                      validator: validateDateNotInPast(form, 'packageStart'),
                    },
                  ]}
                  validateTrigger="onChange"
                >
                  <DatePicker className="w-full !h-12" />
                </Form.Item>
              </div>
              <Divider className="border-t-2 border-black border-solid" />
            </Col>
            <Col span={3}>
              <Row>End Date</Row>
              <Divider className="border-t-2 border-black border-solid" />
              <div className="p-3 h-24">
                <Form.Item
                  name="packageEnd"
                  rules={[{ required: true }]}
                  help={false}
                >
                  <DatePicker className="w-full !h-12" />
                </Form.Item>
              </div>
              <Divider className="border-t-2 border-black border-solid" />
            </Col>
            <Col span={6}>
              <Row>Package</Row>
              <Divider className="border-t-2 border-black border-solid" />
              <div className="p-3 h-24">
                <Form.Item
                  name="packageID"
                  rules={[{ required: true }]}
                  help={false}
                >
                  <Select
                    className="w-full"
                    size="large"
                    showSearch
                    placeholder="Select a Package"
                    options={filteredResults.map((field) => ({
                      value: field.value,
                      label: (
                        <div className="break-words whitespace-normal">
                          {field.label}
                        </div>
                      ),
                      searchLabel: field.label,
                    }))}
                    notFoundContent={<Empty />}
                    onSearch={setSearchPackageQuery}
                    filterOption={false}
                  />
                </Form.Item>
              </div>
              <Divider className="border-t-2 border-black border-solid" />
            </Col>
            <Col span={6}>
              <Tooltip title="Sets the allowed email domains for users on this team. When signing up via Link Based Signup or Invitation, the user's email must match one of these domains.">
                <Row>Domain Whitelist</Row>
              </Tooltip>
              <Divider className="border-t-2 border-black border-solid" />
              <div className="p-3 h-24">
                <Form.Item
                  name="domains"
                  rules={[
                    {
                      validator: (_, value) => domainValidator(value),
                      message:
                        'Invalid domain. One or more domains are invalid.',
                    },
                  ]}
                >
                  <Input
                    className="!h-12"
                    placeholder="Enter domains separated by commas (e.g., example.com, another.com)"
                  />
                </Form.Item>
              </div>
              <Divider className="border-t-2 border-black border-solid" />
            </Col>
          </Row>
          <Row justify="end">
            <Col xl={{ span: 3 }} md={{ span: 5 }} sm={{ span: 8 }}>
              <div className="p-4">
                <Button
                  type="text"
                  className="w-full"
                  onClick={onClose}
                  loading={isLoading}
                >
                  Cancel
                </Button>
              </div>
            </Col>
            <Col xl={{ span: 3 }} md={{ span: 5 }} sm={{ span: 8 }}>
              <div className="p-4">
                <Button
                  type="primary"
                  className="w-full"
                  onClick={handleSubmit}
                  loading={isLoading}
                >
                  Create Team
                </Button>
              </div>
            </Col>
          </Row>
        </Form>
      </Drawer>
    </>
  );
};
